import type { OutputsSettings } from '@glyph/protocol-ts';
import { useDevice, useSaveSettings, useStatus, useSystemInfo } from '@glyph/core';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  ColorBar,
  FactRow,
  Field,
  KeyValueRow,
  ListRow,
  Note,
  PageHeader,
  Section,
  SegmentedControl,
  Select,
  StatusPill,
  SwitchRow,
  useToast,
} from '@glyph/ui';
import { LAYOUT_OPTIONS, MODE_OVERRIDES, fmtMode, layoutLabel, outputByName } from './shared';

export interface OutputsScreenProps {
  deviceId?: string;
}

/** Outputs: HDMI-A/B sink + mode + layout, Mirror/Independent, detection, boot colour (with the honest HDMI re-sync row). */
export function OutputsScreen({ deviceId }: OutputsScreenProps) {
  const dev = useDevice(deviceId);
  const status = useStatus(deviceId);
  const info = useSystemInfo(deviceId);
  const s = useSaveSettings(deviceId);
  const toast = useToast();
  const o = s.draft?.outputs;
  if (!o) return <Section />;
  const up = (p: Partial<OutputsSettings>) => s.update('outputs', p);
  const dirty = s.isDirty('outputs');
  const outs = status.data?.outputs;
  const outA = outputByName(outs, 'HDMI-A');
  const outB = outputByName(outs, 'HDMI-B');
  const mirror = o.policy === 'mirror';
  const assign = (c: string, v: string) => up({ assignments: { ...o.assignments, [c]: v } });
  const save = async () => {
    await s.save(['outputs']);
    toast.push('Output settings applied', 'ok');
  };
  const policySeg = (fw?: boolean) => (
    <SegmentedControl
      fullWidth={fw}
      value={o.policy}
      onChange={(v) => up({ policy: v })}
      options={[
        { value: 'mirror', label: 'Mirror' },
        { value: 'independent', label: 'Independent feeds' },
      ]}
    />
  );
  const feedSelect = (c: string, block: boolean) => (
    <Select
      block={block}
      value={o.assignments[c] ?? 'main'}
      onChange={(v) => assign(c, v)}
      options={[...LAYOUT_OPTIONS]}
      aria-label={`${c} feed`}
    />
  );
  const bootColor = info.data?.boot_color ?? s.draft?.display.key_color ?? '#00B140';
  const bootRows = (
    <>
      <FactRow inset label="Rainbow splash" value="DISABLED" meta />
      <FactRow inset label="Kernel console on HDMI" value="OFF" meta />
      <FactRow inset label="Power-on to colour" value="≈ 4.2 s" meta />
      <FactRow inset label="HDMI re-sync on reboot" value="1–2 s · UNAVOIDABLE" meta valueTone="warn" />
    </>
  );
  const bootNote = (
    <Note>
      The signal itself drops while HDMI renegotiates. Downstream switchers with freeze-on-loss hide it; a
      service restart (no reboot) does not drop the signal at all. The boot colour is baked into the OS image
      (kernel cmdline) and matches the caption key colour.
    </Note>
  );
  const cards = [
    {
      name: 'HDMI-A',
      hint: 'port nearest USB-C',
      out: outA,
      feed: (block: boolean) => feedSelect('HDMI-A', block),
    },
    {
      name: 'HDMI-B',
      hint: 'port nearest Ethernet',
      out: outB,
      feed: (block: boolean) =>
        mirror ? (
          <span
            className={block ? 'text-base text-fg3' : 'flex-none text-right font-mono text-base text-fg3'}
          >
            {block ? 'Same as HDMI-A · switch to Independent to assign' : 'Same as HDMI-A'}
          </span>
        ) : (
          feedSelect('HDMI-B', block)
        ),
    },
  ];

  return (
    <Section>
      <PageHeader
        desktopOnly
        title="Display outputs"
        meta={`${dev.data?.name ?? ''} · headless DRM/KMS renderer · hot-plug detection ${o.auto_detect ? 'on' : 'off'}`}
        actions={
          <>
            {policySeg()}
            <Button variant="primary" disabled={!dirty || s.isSaving} onClick={() => void save()}>
              {dirty ? 'Apply' : 'Applied'}
            </Button>
          </>
        }
      />
      {/* mobile */}
      <div className="flex flex-col gap-4 md:hidden">
        {policySeg(true)}
        {cards.map((c) => (
          <Card key={c.name}>
            <CardHeader
              title={c.name}
              hint={c.hint}
              actions={
                <StatusPill tone={c.out?.connected ? 'ok' : 'fg5'}>
                  {c.out?.connected ? 'CONNECTED' : 'NO SINK'}
                </StatusPill>
              }
            />
            <ListRow label="MODE" title={fmtMode(c.out)} />
            <ListRow label="LAYOUT" title={layoutLabel(c.out?.layout ?? o.assignments[c.name] ?? 'main')} />
            <ListRow label="FEED" trailing={c.feed(false)} />
          </Card>
        ))}
        <Card>
          <CardHeader title="Detection" />
          <SwitchRow
            title="Auto-detect on hot-plug"
            checked={o.auto_detect}
            onChange={(v) => up({ auto_detect: v })}
          />
          <ListRow
            title="Force output mode"
            trailing={
              <Select
                value={o.mode_override}
                onChange={(v) => up({ mode_override: v })}
                options={[...MODE_OVERRIDES]}
              />
            }
          />
          <SwitchRow
            title="Keep signal when sink is lost"
            checked={o.keep_signal_on_loss}
            onChange={(v) => up({ keep_signal_on_loss: v })}
          />
        </Card>
        <Card>
          <CardHeader title="Boot colour" meta={bootColor.toUpperCase()} />
          <div className="flex flex-col gap-3 px-4 pb-3.5">
            <ColorBar color={bootColor} />
            {bootRows}
            {bootNote}
          </div>
        </Card>
        <Button size="lg" variant="primary" disabled={!dirty || s.isSaving} onClick={() => void save()}>
          {dirty ? 'Apply output settings' : 'Applied'}
        </Button>
      </div>
      {/* desktop */}
      <div
        className="hidden items-start gap-5 md:grid"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}
      >
        {cards.map((c) => (
          <Card key={c.name}>
            <div className="flex items-center justify-between p-4">
              <div className="flex flex-col gap-[5px]">
                <span className="text-md font-medium leading-none">{c.name}</span>
                <span className="font-mono text-xs text-fg4">{c.hint}</span>
              </div>
              <StatusPill tone={c.out?.connected ? 'ok' : 'fg5'}>
                {c.out?.connected ? 'CONNECTED' : 'NO SINK'}
              </StatusPill>
            </div>
            <KeyValueRow
              k="Mode"
              value={fmtMode(c.out)}
              sub={
                c.out?.connected
                  ? 'adopted from the kernel mode · no modeset · no HDMI re-sync'
                  : 'plug a sink to detect its preferred mode'
              }
            />
            <KeyValueRow k="Layout" value={layoutLabel(c.out?.layout ?? o.assignments[c.name] ?? 'main')} />
            <KeyValueRow k="Feed" value={c.feed(true)} />
          </Card>
        ))}
        <Card mode="padded">
          <CardTitle>Detection</CardTitle>
          <SwitchRow
            first
            title="Auto-detect on hot-plug"
            checked={o.auto_detect}
            onChange={(v) => up({ auto_detect: v })}
            className="border-t-0 py-0"
          />
          <Field label="Force output mode">
            <Select
              block
              value={o.mode_override}
              onChange={(v) => up({ mode_override: v })}
              options={[...MODE_OVERRIDES]}
            />
          </Field>
          <SwitchRow
            first
            title="Keep signal when sink is lost"
            checked={o.keep_signal_on_loss}
            onChange={(v) => up({ keep_signal_on_loss: v })}
            className="border-t-0 py-0"
          />
          {o.mode_override && (
            <Note tone="warn">A forced mode causes exactly one modeset at startup (HDMI re-sync).</Note>
          )}
        </Card>
        <Card mode="padded">
          <CardTitle meta={bootColor.toUpperCase()}>Boot colour</CardTitle>
          <ColorBar color={bootColor} />
          <div className="flex flex-col">{bootRows}</div>
          {bootNote}
        </Card>
      </div>
    </Section>
  );
}
